"use client";

import { useEffect } from "react";
import { I18nextProvider } from "react-i18next";
import i18n, { supportedLocales, type SupportedLocale } from "@/i18n";

function detectBrowserLocale(): SupportedLocale | undefined {
  const detected = i18n.services.languageDetector?.detect();
  const candidates = Array.isArray(detected) ? detected : [detected];

  return candidates
    .filter((lng): lng is string => typeof lng === "string")
    .map((lng) => lng.split("-")[0])
    .find((lng): lng is SupportedLocale =>
      (supportedLocales as readonly string[]).includes(lng),
    );
}

export function I18nProvider({ children }: { children: React.ReactNode }) {
  useEffect(() => {
    // Language detection is deferred until after hydration: pages are
    // statically rendered in French, and switching languages any earlier
    // would make the hydration render mismatch the server HTML.
    const locale = detectBrowserLocale();
    if (locale && locale !== i18n.language) {
      i18n.changeLanguage(locale);
    }

    const syncHtmlLang = (lng: string) => {
      document.documentElement.lang = lng;
    };
    syncHtmlLang(i18n.language);
    i18n.on("languageChanged", syncHtmlLang);
    return () => i18n.off("languageChanged", syncHtmlLang);
  }, []);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
